import {
  ApplicationConfig,
  ErrorHandler,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient } from '@angular/common/http';
import { provideTransloco, TranslocoService, getBrowserLang } from '@jsverse/transloco';

import { WHITE_LABEL_CONFIG } from '@crewdle/white-label/angular';
import { HOME_HUB_CONFIG } from '@crewdle/home-hub/angular';
import { routes } from './app.routes';
import { TranslocoHttpLoader } from '../transloco-loader';
import { AppErrorHandler } from './core/error-handler';
import { FirebaseService } from './core/services/firebase.service';
import { TeamsService } from './core/services/teams.service';
import { environment } from '../environments/environment';
import { getStoredLanguage, normalizeLanguage } from './core/translation.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: AppErrorHandler },
    provideRouter(routes),
    provideHttpClient(),
    provideTransloco({
      config: {
        availableLangs: ['en', 'fr'],
        defaultLang: 'fr',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    // Firestore instance for @crewdle/white-label's WhiteLabelService.
    {
      provide: WHITE_LABEL_CONFIG,
      useFactory: () => ({ firestore: inject(FirebaseService).firestore! }),
    },
    // Firestore instance + domain for @crewdle/home-hub's HomeHubComponent
    // (see HomeHubOverlayComponent).
    {
      provide: HOME_HUB_CONFIG,
      useFactory: () => ({
        firestore: inject(FirebaseService).firestore!,
        domain: environment.production ? 'crewdle.ai' : 'stg.crewdle.ai',
      }),
    },
    provideAppInitializer(() => {
      const translate = inject(TranslocoService);
      const savedLang = getStoredLanguage();

      if (savedLang) {
        translate.setActiveLang(savedLang);
        return;
      }

      // Check root domain cookie
      try {
        const cookies = document.cookie.split(';');
        for (const c of cookies) {
          const t = c.trim();
          if (t.startsWith('crewdle_lang=')) {
            const v = decodeURIComponent(t.substring('crewdle_lang='.length));
            const language = normalizeLanguage(v);
            if (language) {
              translate.setActiveLang(language);
              localStorage.setItem('lang', language);
              return;
            }
          }
        }
      } catch {}

      const browserLang = normalizeLanguage(getBrowserLang());
      if (browserLang) {
        translate.setActiveLang(browserLang);
      }
    }),
    provideAppInitializer(() => {
      // Warm Teams detection early (non-blocking) so the auth guard's awaited
      // ensureTeamsDetected() resolves instantly. Skipped internally when not
      // framed. Deliberately NOT awaited — must never delay app startup.
      inject(TeamsService).initializeTeams();
    }),
  ],
};
